import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { AgentProfileDialog } from './users/AgentProfileDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    House,
    FileText,
    Users,
    Gear,
    SignOut,
    FlowArrow,
    Folder,
    Bell,
    ChartBar,
    UserCircle,
    CaretLeft,
    CaretRight,
    CaretDown,
    Package,
    Cube,
    Calculator,
    Snowflake,
    Tag,
    Table,
    PaintBrush,
    Storefront,
} from '@phosphor-icons/react';

const navigation = [
    { name: 'Pannello di Controllo', href: '/', icon: House },
    { name: 'Preventivi', href: '/preventivi', icon: Calculator },
    { name: 'Contratti', href: '/contratti', icon: FileText },
    { name: 'Clienti', href: '/clienti', icon: Users },
    { name: 'Documenti', href: '/documenti', icon: Folder },
    { name: 'Reportistica', href: '/report', icon: ChartBar },
];

// Store HVAC navigation - visible to admin, responsabile_store and store_agent
const storeNavigation = [
    { name: 'Store HVAC', href: '/store', icon: Snowflake, storeAllowed: true },
    { name: 'Prodotti Store', href: '/store/prodotti', icon: Package, catalogManager: true },
    { name: 'Modifiche Rapide', href: '/store/modifiche-rapide', icon: Table, catalogManager: true },
    { name: 'Branding PDF', href: '/store/branding-pdf', icon: PaintBrush, adminOnly: true },
    { name: 'Promozioni Store', href: '/store/promozioni', icon: Tag, catalogManager: true },
];

const adminNavigation = [
    { name: 'Workflow Builder', href: '/workflow-console', icon: FlowArrow, adminOnly: true },
    { name: 'Listino Materiali', href: '/listino-materiali', icon: Package, logistics: true },
    { name: 'Inventario', href: '/inventario', icon: Cube, logistics: true },
    { name: 'Gestione Utenti', href: '/utenti', icon: UserCircle, adminOnly: true },
    { name: 'Audit Log', href: '/audit', icon: Gear, adminOnly: true },
];

export default function Layout({ children }) {
    const [collapsed, setCollapsed] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const { user, logout, isAdmin, canAccessInventory, canAccessStore, isStoreAgent, isStoreManager } = useAuth();
    const location = useLocation();

    const isActive = (href) => {
        if (href === '/') return location.pathname === '/';
        return location.pathname.startsWith(href);
    };

    // Filter navigation based on role - store_agent ONLY sees Store HVAC,
    // responsabile_store sees Preventivi PV + Store HVAC
    const filteredNavigation = isStoreAgent
        ? []
        : isStoreManager
            ? navigation.filter((item) => item.href === '/preventivi')
            : navigation;

    const visibleStoreNav = useMemo(() => storeNavigation.filter(item => {
        if (isAdmin) return true;
        if (isStoreManager && (item.storeAllowed || item.catalogManager)) return true;
        if (canAccessStore && item.storeAllowed) return true;
        return false;
    }), [isAdmin, isStoreManager, canAccessStore]);

    const visibleAdminNav = useMemo(() => adminNavigation.filter(item => {
        // Admin vede tutto
        if (isAdmin) return true;
        // Logistica vede solo le voci con logistics: true
        if (canAccessInventory && item.logistics) return true;
        return false;
    }), [isAdmin, canAccessInventory]);

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <aside 
                className={cn(
                    "bg-white border-r border-border flex flex-col transition-all duration-300",
                    collapsed ? "w-16" : "w-64"
                )}
            >
                {/* Logo Main Solution */}
                <div className="h-16 border-b border-border flex items-center px-3 gap-3">
                    <div className="w-9 h-9 bg-white rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-slate-100">
                        <img
                            src="/mainsolution_logo.jpg"
                            alt="Main Solution"
                            className="w-full h-full object-contain p-0.5"
                        />
                    </div>
                    {!collapsed && (
                        <div className="flex flex-col leading-tight">
                            <span className="font-heading font-bold text-[15px] text-slate-900 tracking-tight">
                                Main Solution
                            </span>
                            <span className="font-body text-[10px] uppercase tracking-[1.5px] text-cyan-600 font-semibold">
                                CRM · Energie Rinnovabili
                            </span>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-2 space-y-1">
                    {filteredNavigation.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                data-testid={`nav-${item.href.replace('/', '') || 'home'}`}
                                className={cn(
                                    "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                                    isActive(item.href)
                                        ? "bg-primary text-primary-foreground"
                                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                )}
                            >
                                <Icon weight={isActive(item.href) ? "fill" : "regular"} className="w-5 h-5 flex-shrink-0" />
                                {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
                            </Link>
                        );
                    })}

                    {/* Store HVAC Section */}
                    {canAccessStore && (
                        <>
                            <div className={cn("py-3", collapsed ? "px-2" : "px-3")}>
                                <div className="border-t border-border" />
                            </div>
                            {!collapsed && (
                                <p className="px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                                    Store HVAC
                                </p>
                            )}
                            {visibleStoreNav
                                .map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        data-testid={`nav-${item.href.replace(/\//g, '-').slice(1)}`}
                                        className={cn(
                                            "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                                            isActive(item.href)
                                                ? "bg-cyan-600 text-white"
                                                : "text-muted-foreground hover:bg-cyan-50 hover:text-cyan-700"
                                        )}
                                    >
                                        <Icon weight={isActive(item.href) ? "fill" : "regular"} className="w-5 h-5 flex-shrink-0" />
                                        {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
                                    </Link>
                                );
                            })}
                        </>
                    )}

                    {(isAdmin || canAccessInventory) && (
                        <>
                            <div className={cn("py-3", collapsed ? "px-2" : "px-3")}>
                                <div className="border-t border-border" />
                            </div>
                            {!collapsed && (
                                <p className="px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                                    {isAdmin ? 'Amministrazione' : 'Logistica'}
                                </p>
                            )}
                            {visibleAdminNav
                                .map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        data-testid={`nav-${item.href.replace('/', '')}`}
                                        className={cn(
                                            "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                                            isActive(item.href)
                                                ? "bg-primary text-primary-foreground"
                                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                        )}
                                    >
                                        <Icon weight={isActive(item.href) ? "fill" : "regular"} className="w-5 h-5 flex-shrink-0" />
                                        {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
                                    </Link>
                                );
                            })}
                        </>
                    )}
                </nav>

                {/* User section — nascosta per store_agent (spostata in topbar) */}
                {!isStoreAgent && (
                    <div className="border-t border-border p-3">
                        {!collapsed ? (
                            <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-muted/50 hover:bg-muted transition-colors">
                                <button
                                    type="button"
                                    onClick={() => setProfileOpen(true)}
                                    className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00BCE7] focus:ring-offset-1 rounded-md"
                                    title="Modifica il mio profilo"
                                    data-testid="open-profile-btn"
                                >
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                                        <UserCircle weight="fill" className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{user?.name}</p>
                                        <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
                                    </div>
                                </button>
                                <button
                                    onClick={logout}
                                    data-testid="logout-button"
                                    className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                    title="Esci"
                                >
                                    <SignOut className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <button
                                    type="button"
                                    onClick={() => setProfileOpen(true)}
                                    className="w-full p-2 rounded-md hover:bg-primary/10 text-primary transition-colors flex justify-center"
                                    title="Modifica il mio profilo"
                                    data-testid="open-profile-btn-collapsed"
                                >
                                    <UserCircle weight="fill" className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={logout}
                                    data-testid="logout-button-collapsed"
                                    className="w-full p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex justify-center"
                                    title="Esci"
                                >
                                    <SignOut className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Collapse button */}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    data-testid="sidebar-collapse-button"
                    className="h-12 border-t border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                    {collapsed ? <CaretRight className="w-5 h-5" /> : <CaretLeft className="w-5 h-5" />}
                </button>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="h-16 bg-white border-b border-border flex items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <h1 className="font-heading text-xl font-semibold text-foreground">
                            {navigation.find(n => isActive(n.href))?.name || 
                             adminNavigation.find(n => isActive(n.href))?.name ||
                             (location.pathname.includes('/workflow-console') ? 'Workflow Builder' : 'Dettaglio')}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/notifiche"
                            data-testid="notifications-button"
                            className="relative p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <Bell className="w-5 h-5" />
                        </Link>

                        {/* Agent pill (solo store_agent) */}
                        {isStoreAgent && user && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-slate-50 border border-slate-200 hover:border-[#00BCE7] hover:bg-cyan-50/40 transition-all focus:outline-none focus:ring-2 focus:ring-[#00BCE7] focus:ring-offset-1"
                                        data-testid="agent-pill-trigger"
                                        title="Apri menu profilo"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0B2A4A] to-[#00BCE7] text-white text-xs font-bold flex items-center justify-center">
                                            {(user.name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || 'U'}
                                        </div>
                                        <div className="flex flex-col text-left leading-tight min-w-0 max-w-[180px]">
                                            <span className="text-[12px] font-semibold text-[#0B2A4A] truncate">{user.name}</span>
                                            <span className="text-[10px] text-slate-500 truncate">
                                                {user.store_info?.store_name || 'Agente Store'}
                                            </span>
                                        </div>
                                        <CaretDown className="w-3 h-3 text-slate-400 shrink-0" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64" data-testid="agent-pill-menu">
                                    <DropdownMenuLabel>
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0B2A4A] to-[#00BCE7] text-white text-sm font-bold flex items-center justify-center">
                                                {(user.name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || 'U'}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[13px] font-semibold text-[#0B2A4A] truncate">{user.name}</p>
                                                <p className="text-[11px] font-normal text-slate-500 truncate">{user.email}</p>
                                            </div>
                                        </div>
                                    </DropdownMenuLabel>
                                    {user.store_info?.store_name && (
                                        <div className="px-2 py-1.5 text-[11px] text-slate-600 flex items-center gap-1.5">
                                            <Storefront className="w-3.5 h-3.5 text-[#00BCE7]" />
                                            <span className="truncate">{user.store_info.store_name}{user.store_info.city ? ` · ${user.store_info.city}` : ''}</span>
                                        </div>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => setProfileOpen(true)}
                                        className="cursor-pointer"
                                        data-testid="menu-item-profile"
                                    >
                                        <UserCircle weight="fill" className="w-4 h-4 mr-2 text-[#00BCE7]" />
                                        Il mio profilo
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={logout}
                                        className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                                        data-testid="menu-item-logout"
                                    >
                                        <SignOut className="w-4 h-4 mr-2" />
                                        Esci
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-auto p-6">
                    {children}
                </main>
            </div>

            {/* Profile dialog (self-service) */}
            <AgentProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
        </div>
    );
}
